import { slideDown, slideUp } from '../../utils/slide-functions';

class MainNav {
  private element: HTMLElement;
  private menu: HTMLElement;
  private utilityItems: NodeListOf<HTMLElement>;
  private toggles: HTMLButtonElement[] = [];
  private backButtons: HTMLButtonElement[] = [];
  private mediaQuery: string;
  constructor(element: HTMLElement) {
    this.element = element;
    this.menu = this.element.querySelector('.main-nav__menu') as HTMLElement;
    this.utilityItems = document.querySelectorAll(".site-header .utility-nav__item[data-level='1']");
    // Must match $bp-l (72em) where the hamburger hides and desktop nav styles apply
    this.mediaQuery = '(min-width: 72em)';
    this.init();
  }

  init() {
    this.setupUtilityItems();
    this.setToggles();
    this.bindEvents();
    if (window.matchMedia(this.mediaQuery).matches) {
      this.setWrapperHeight();
      this.stickyNav();
      window.addEventListener('scroll', this.stickyNav.bind(this));
      document.addEventListener('click', this.handleOutsideClick.bind(this));
    }
  }

  private setToggles() {
    this.element.querySelectorAll('[nav-toggle]').forEach((toggle) => {
      this.toggles.push(toggle as HTMLButtonElement);
    });
    this.element.querySelectorAll('[nav-toggle-back]').forEach((backButton) => {
      this.backButtons.push(backButton as HTMLButtonElement);
    });
  }

  private setupUtilityItems() {
    this.utilityItems.forEach((item) => {
      const clone = item.cloneNode(true) as HTMLElement;
      const cloneLink = clone.querySelector('.utility-nav__link') as HTMLElement;
      const hasToggle = clone.querySelector('[nav-toggle]') as HTMLButtonElement;

      clone.classList.add('main-nav__item');
      cloneLink.classList.add('main-nav__link');

      if (hasToggle) {
        const itemToggles = clone.querySelectorAll('[nav-toggle]');
        const itemSubItems = clone.querySelectorAll('.utility-nav__item');

        itemSubItems.forEach((subItem: Element) => {
          const links = subItem.querySelectorAll('.utility-nav__link');
          links.forEach((link: Element) => {
            link.classList.add('main-nav__link');
          });
          subItem.classList.add('main-nav__item');
        });

        itemToggles.forEach((toggle: Element, index: number) => {
          const newToggleId = `utility-nav-toggle-${index}`;
          const newTarget = `utility-nav-sub-${index}`;
          const toggleTarget = toggle.nextElementSibling as HTMLElement;
          const toggleTargetBack = toggleTarget.querySelector('[nav-toggle-back]') as HTMLButtonElement;

          toggle.classList.add('main-nav__link--toggle');
          toggle.id = newToggleId;
          (toggle as HTMLButtonElement).dataset.target = newTarget;
          toggleTargetBack.classList.add('main-nav__link--back');
          toggleTargetBack.dataset.target = newTarget;
          toggleTarget.classList.add('main-nav__sub');
          toggleTarget.id = newTarget;
        });
      }

      (this.menu as HTMLElement).appendChild(clone);
    });
  }

  private bindEvents() {
    this.element.querySelectorAll('[nav-toggle]').forEach((toggle) => {
      (toggle as HTMLButtonElement).addEventListener('click', this.handleToggleClick.bind(this));
    });
    this.element.querySelectorAll('[nav-toggle-back]').forEach((backButton) => {
      (backButton as HTMLButtonElement).addEventListener('click', this.handleBackClick.bind(this));
    });
  }

  private stickyNav() {
    const documentScrollTop = window.scrollY;
    const headerLayoutHeight =
      parseInt(window.getComputedStyle(document.querySelector('.header-layout') as HTMLElement).height) || 0;
    const parent = this.element.parentElement as HTMLElement;

    if (documentScrollTop > headerLayoutHeight) {
      parent.classList.add('sticky');
    } else {
      parent.classList.remove('sticky');
    }
  }

  private setWrapperHeight() {
    if (window.matchMedia(this.mediaQuery).matches) {
      const navHeight = window.getComputedStyle(this.element).height;
      const wrapper = this.element.closest('.header-col--nav') as HTMLElement;

      if (wrapper) {
        wrapper.style.minHeight = navHeight;
      }
    }
  }

  private handleBackClick(event: MouseEvent) {
    event.preventDefault();
    const target = event.target as HTMLButtonElement;
    const targetContent = document.querySelector(`[id="${target.getAttribute('data-target')}"]`) as HTMLElement;
    const relatedToggle = targetContent.previousElementSibling as HTMLButtonElement;
    const parentLink = target.parentElement?.parentElement?.parentElement?.querySelector(
      '.main-nav__link',
    ) as HTMLElement;
    if (parentLink) {
      parentLink.focus();
    }
    this.closeSubmenu(targetContent, relatedToggle);
  }

  private closeAllSubmenus(excludeToggle?: HTMLButtonElement) {
    this.toggles.forEach((toggle) => {
      if (toggle === excludeToggle) {
        return;
      }

      const isFirstLevel = toggle.parentElement?.getAttribute('data-level') === '1';

      const targetContent = document.querySelector(`[id="${toggle.getAttribute('data-target')}"]`) as HTMLElement;

      toggle.classList.remove('active');

      if (isFirstLevel) {
        slideUp(targetContent, 200);
      }

      this.closeSubmenu(targetContent);
    });
  }

  private closeSiblingSubmenus(toggle: HTMLButtonElement) {
    const siblingSubmenus = toggle
      .closest('.main-nav__item[data-level="1"]')
      ?.querySelectorAll('.main-nav__sub.active');
    if (siblingSubmenus) {
      siblingSubmenus.forEach((submenu: Element) => {
        submenu.previousElementSibling?.classList.remove('active');
        this.closeSubmenu(submenu as HTMLElement);
      });
    }
  }

  private openSubmenu(submenu: HTMLElement) {
    submenu.classList.add('active');
    setTimeout(() => {
      submenu.classList.add('animate');
      if (!window.matchMedia(this.mediaQuery).matches) {
        (submenu.querySelector('.main-nav__link') as HTMLElement)?.focus();
      }
    });
  }

  private closeSubmenu(submenu: HTMLElement, relatedToggle?: HTMLButtonElement) {
    if (relatedToggle) {
      relatedToggle.classList.remove('active');
    }
    submenu.classList.remove('animate');
    setTimeout(() => {
      submenu.classList.remove('active');
    }, 300);
  }

  private handleOutsideClick(event: MouseEvent) {
    // Only handle clicks on desktop
    if (!window.matchMedia(this.mediaQuery).matches) {
      return;
    }

    const target = event.target as HTMLElement;

    // Check if the click is outside the navigation element and its children
    if (!this.element.contains(target)) {
      this.closeAllSubmenus();
    }
  }

  private handleToggleClick(event: MouseEvent) {
    event.preventDefault();
    const toggle = event.target as HTMLButtonElement;
    const submenu = document.querySelector(`[id="${toggle.getAttribute('data-target')}"]`) as HTMLElement;
    const isFirstLevel = toggle.parentElement?.getAttribute('data-level') === '1';

    toggle.classList.toggle('active');

    if (toggle.classList.contains('active')) {
      if (window.matchMedia(this.mediaQuery).matches) {
        if (isFirstLevel) {
          this.closeAllSubmenus(toggle);
          slideDown(submenu, 200);
        } else {
          this.closeSiblingSubmenus(toggle);
          this.openSubmenu(submenu);
        }
      } else {
        this.openSubmenu(submenu);
      }
    } else {
      if (window.matchMedia(this.mediaQuery).matches && isFirstLevel) {
        slideUp(submenu, 200);
      } else {
        this.closeSubmenu(submenu);
      }
    }
  }
}

document.querySelectorAll('.main-nav').forEach((element) => new MainNav(element as HTMLElement));
